import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/joy';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { sanitizeNewsHtml, stripHtml } from '../../utils/sanitizeHtml';

const BOTTOM_PAD = 96;   // место под панель кнопок
const TOP_PAD = 44;      // место под прогресс-бар

/** Общие стили для HTML из редактора */
const htmlSx = (color) => ({
    color,
    fontSize: '0.95rem',
    lineHeight: 1.55,
    '& p': { margin: '0 0 10px' },
    '& h1, & h2, & h3, & h4': { margin: '14px 0 8px', fontWeight: 800, lineHeight: 1.25 },
    '& h1': { fontSize: '1.35rem' },
    '& h2': { fontSize: '1.18rem' },
    '& h3': { fontSize: '1.05rem' },
    '& ul, & ol': { margin: '0 0 10px', paddingLeft: '20px' },
    '& li': { margin: '4px 0' },
    '& a': { color: 'inherit', textDecoration: 'underline' },
    '& img': { maxWidth: '100%', borderRadius: '12px', margin: '8px 0' },
    '& blockquote': {
        margin: '10px 0', padding: '6px 12px',
        borderLeft: '3px solid currentColor', opacity: 0.9,
    },
    '& hr': { border: 'none', borderTop: '1px solid currentColor', opacity: 0.25, margin: '14px 0' },
    '& table': { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
    '& td, & th': { border: '1px solid rgba(128,128,128,0.35)', padding: '6px 8px' },
});

/** Горизонтальная карусель картинок для формата «Стандарт» */
function ImagesStrip({ images = [], radius = 16 }) {
    const scrollRef = useRef(null);
    const [active, setActive] = useState(0);

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el || !el.clientWidth) return;
        setActive(Math.round(el.scrollLeft / el.clientWidth));
    };

    if (!images.length) return null;

    return (
        <Box sx={{ mb: 1.6 }}>
            <Box
                ref={scrollRef}
                onScroll={onScroll}
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    borderRadius: `${radius}px`,
                    gap: 0,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                {images.map((img) => (
                    <Box key={img.id} sx={{
                        flex: '0 0 100%',
                        scrollSnapAlign: 'start',
                        aspectRatio: '4 / 3',
                        background: 'rgba(128,128,128,0.12)',
                        borderRadius: `${radius}px`,
                        overflow: 'hidden',
                    }}>
                        <Box
                            component="img"
                            src={img.url}
                            alt={img.caption || ''}
                            loading="lazy"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    </Box>
                ))}
            </Box>

            {images.length > 1 && (
                <Stack direction="row" spacing={0.6} justifyContent="center" sx={{ mt: 1 }}>
                    {images.map((img, i) => (
                        <Box key={img.id} sx={{
                            width: i === active ? 16 : 6,
                            height: 6,
                            borderRadius: 6,
                            background: i === active ? 'currentColor' : 'rgba(128,128,128,0.4)',
                            transition: 'all .22s',
                        }} />
                    ))}
                </Stack>
            )}

            {images[active]?.caption && (
                <Typography level="body-xs" sx={{ textAlign: 'center', mt: 0.8, opacity: 0.7, color: 'inherit' }}>
                    {images[active].caption}
                </Typography>
            )}
        </Box>
    );
}


export default function NewsSlideContent({
    slide,
    expanded = false,
    onToggleExpand,
    accentColor = '#572AC3',
}) {
    const [bgFailed, setBgFailed] = useState(false);

    useEffect(() => { setBgFailed(false); }, [slide?.id]);

    if (!slide) return null;

    const isFull = slide.layout === 'full_image';
    const html = sanitizeNewsHtml(slide.content || '');
    const plain = slide.short_text || stripHtml(slide.content || '');

    // ── Формат «Фул картинка» ─────────────────────────────────────────────────
    if (isFull) {
        const overlay = Math.min(0.9, Math.max(0, slide.overlay_opacity ?? 0.45));
        const hasBg = !!slide.background_image_url && !bgFailed;

        return (
            <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                {hasBg ? (
                    <Box
                        component="img"
                        src={slide.background_image_url}
                        alt=""
                        onError={() => setBgFailed(true)}
                        sx={{
                            position: 'absolute', inset: 0,
                            width: '100%', height: '100%',
                            objectFit: 'cover', display: 'block',
                        }}
                    />
                ) : (
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        background: `linear-gradient(155deg, ${accentColor} 0%, #16121f 100%)`,
                    }} />
                )}

                <Box sx={{
                    position: 'absolute', inset: 0,
                    background: `linear-gradient(to top,
                        rgba(0,0,0,${Math.min(0.95, overlay + 0.35)}) 0%,
                        rgba(0,0,0,${overlay}) 38%,
                        rgba(0,0,0,0.12) 100%)`,
                }} />

                <Box
                    onClick={onToggleExpand}
                    sx={{
                        position: 'absolute',
                        left: 0, right: 0, bottom: `${BOTTOM_PAD}px`,
                        px: 2.2, pt: 2, pb: 1.4,
                        maxHeight: expanded ? '68%' : '40%',
                        overflowY: expanded ? 'auto' : 'hidden',
                        cursor: 'pointer',
                        zIndex: 4,
                        background: expanded ? 'rgba(14,14,20,0.62)' : 'transparent',
                        backdropFilter: expanded ? 'blur(16px)' : 'none',
                        WebkitBackdropFilter: expanded ? 'blur(16px)' : 'none',
                        borderTopLeftRadius: expanded ? '20px' : 0,
                        borderTopRightRadius: expanded ? '20px' : 0,
                        transition: 'max-height .3s cubic-bezier(.22,.9,.3,1), background .3s ease',
                        '&::-webkit-scrollbar': { width: '3px' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.3)', borderRadius: 3 },
                    }}
                >
                    {slide.title && (
                        <Typography sx={{
                            color: '#fff', fontWeight: 800,
                            fontSize: '1.25rem', lineHeight: 1.25, mb: 1,
                            textShadow: '0 2px 12px rgba(0,0,0,0.35)',
                        }}>
                            {slide.title}
                        </Typography>
                    )}

                    {expanded ? (
                        <Box sx={htmlSx('rgba(255,255,255,0.94)')} dangerouslySetInnerHTML={{ __html: html }} />
                    ) : (
                        <Typography sx={{
                            color: 'rgba(255,255,255,0.92)',
                            fontSize: '0.95rem', lineHeight: 1.5,
                            display: '-webkit-box', WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            textShadow: '0 1px 8px rgba(0,0,0,0.3)',
                        }}>
                            {plain}
                        </Typography>
                    )}

                    {(html || plain) && (
                        <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 1.1, opacity: 0.85 }}>
                            {expanded
                                ? <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
                                : <ExpandLessRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />}
                            <Typography level="body-xs" sx={{ color: '#fff', fontWeight: 600 }}>
                                {expanded ? 'Свернуть' : 'Подробнее'}
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Box>
        );
    }

    // ── Формат «Стандарт» ─────────────────────────────────────────────────────
    const textColor = slide.text_color || '#111111';

    return (
        <Box sx={{
            position: 'absolute', inset: 0,
            background: slide.background_color || '#FFFFFF',
            color: textColor,
            overflowY: 'auto',
            px: 2.2,
            pt: `${TOP_PAD}px`,
            pb: `${BOTTOM_PAD}px`,
            '&::-webkit-scrollbar': { width: '3px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.18)', borderRadius: 3 },
        }}>
            <ImagesStrip images={slide.images || []} />

            {slide.title && (
                <Typography sx={{
                    fontWeight: 800, fontSize: '1.3rem',
                    lineHeight: 1.25, mb: 1.2, color: textColor,
                }}>
                    {slide.title}
                </Typography>
            )}

            {slide.short_text && (
                <Typography sx={{ fontSize: '0.98rem', fontWeight: 600, opacity: 0.85, mb: 1.2, color: textColor }}>
                    {slide.short_text}
                </Typography>
            )}

            <Box sx={htmlSx(textColor)} dangerouslySetInnerHTML={{ __html: html }} />
        </Box>
    );
}